import React, { useState } from "react";
import { getAISuggestions, getContentGaps } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, PaperPlaneTilt, Lightning, TrendUp, CalendarBlank, Users } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const QUICK_PROMPTS = [
  { icon: TrendUp, label: "Content Gap Analysis", prompt: "Analyze my channel content gaps and suggest which channels need immediate attention based on their posting schedules and pipeline status." },
  { icon: CalendarBlank, label: "Smart Scheduling", prompt: "Based on my current pipeline and posting schedules, suggest optimal scheduling for videos that are ready but not scheduled." },
  { icon: Lightning, label: "Video Ideas", prompt: "Generate 5 trending video ideas for my YouTube channels based on their niches and current content themes." },
  { icon: Users, label: "Workload Balance", prompt: "Analyze my editor workloads and suggest how to better distribute video assignments for optimal efficiency." },
];

export default function AIInsights() {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [contentGaps, setContentGaps] = useState(null);
  const [loadingGaps, setLoadingGaps] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    setResponse("");

    try {
      const result = await getAISuggestions(prompt);
      setResponse(result.data.suggestion);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI service unavailable");
      setResponse("Sorry, the AI service is temporarily unavailable. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (quickPrompt) => {
    setPrompt(quickPrompt);
  };

  const handleAnalyzeGaps = async () => {
    setLoadingGaps(true);
    try {
      const result = await getContentGaps();
      setContentGaps(result.data);
    } catch (err) {
      toast.error("Failed to analyze content gaps");
    } finally {
      setLoadingGaps(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Sparkle size={24} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">AI Insights</h1>
          <p className="text-gray-400 text-sm">Powered by Claude Sonnet 4.5</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {QUICK_PROMPTS.map((item, idx) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={() => handleQuickPrompt(item.prompt)}
              className="surface-card p-4 text-left hover:border-purple-500/30 transition-all group"
              data-testid={`quick-prompt-${idx}`}
            >
              <Icon size={24} className="text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="text-white font-medium text-sm">{item.label}</h3>
            </button>
          );
        })}
      </div>

      {/* Chat Interface */}
      <div className="surface-card p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask me anything about your YouTube channels, content strategy, scheduling, or team management..."
              className="bg-white/5 border-white/10 text-white min-h-[120px] resize-none"
              data-testid="ai-prompt-input"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" className="btn-primary" disabled={loading || !prompt.trim()} data-testid="ai-submit-btn">
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <PaperPlaneTilt size={18} className="mr-2" />
                  Send
                </>
              )}
            </Button>
          </div>
        </form>

        {response && (
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={16} className="text-purple-400" />
              <span className="text-sm font-medium text-purple-400">AI Response</span>
            </div>
            <div className="prose prose-invert prose-sm max-w-none">
              <p className="text-gray-300 whitespace-pre-wrap">{response}</p>
            </div>
          </div>
        )}
      </div>

      {/* Content Gaps Analysis */}
      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium text-white">Automated Content Gap Analysis</h3>
          <Button onClick={handleAnalyzeGaps} variant="outline" className="btn-secondary" disabled={loadingGaps} data-testid="analyze-gaps-btn">
            {loadingGaps ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <TrendUp size={16} className="mr-2" />
                Analyze Now
              </>
            )}
          </Button>
        </div>

        {contentGaps ? (
          <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-3">
              <TrendUp size={16} className="text-blue-400" />
              <span className="text-sm font-medium text-blue-400">Analysis Result</span>
              <span className="text-xs text-gray-500">({contentGaps.channels_analyzed} channels)</span>
            </div>
            <div className="prose prose-invert prose-sm max-w-none">
              <p className="text-gray-300 whitespace-pre-wrap">{contentGaps.analysis}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm text-center py-8">
            Click "Analyze Now" to get AI-powered insights on your content gaps
          </p>
        )}
      </div>

      {/* Tips */}
      <div className="surface-card p-6">
        <h3 className="font-heading text-lg font-medium text-white mb-4">Tips for Better Results</h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start gap-2">
            <span className="text-purple-400">•</span>
            Be specific about which channels or videos you want advice on
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-400">•</span>
            Ask for actionable recommendations with deadlines
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-400">•</span>
            Include context about your content goals and target audience
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-400">•</span>
            Use the quick prompts as starting points and customize them
          </li>
        </ul>
      </div>
    </div>
  );
}
